import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Tabs from '@material-ui/core/Tabs';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import objectSupport from 'dayjs/plugin/objectSupport';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';
import PropTypes from 'prop-types';
import randomString from 'random-string';

import {
    Text,
    ContainedButton,
    PatientDetailTab,
    OutlinedButton,
    QuickNoteTag,
    CallController,
    GoBackButton,
    ConfirmDialog,
    Progress,
    Span,
    KeyboardDatePicker,
    TimePicker,
    StyledFormHelperText,
    Input,
    Checkbox,
    OverlapCard,
    StyledDialog,
    TextField,
} from '../../components/index';

import { StyledBackground } from '../../components/MainLayout/style';

import PatientMedicalRecord from '../../components/Provider/Session/PatientMedicalRecord';
import PatientCard from '../../components/Provider/Session/PatientCard';
import SelectPatientCard from '../../components/Provider/Appointment/PatientCard';
import ProviderNote from '../../components/Provider/Session/ProviderNote';
import HomeworkCard from '../../components/Provider/Session/HomeworkCard';
import Main from '../../components/VideoCall/main';

import {
    alertError,
    alertSuccess,
    // setExpressions,
    clearSelectPatientId,
} from '../../redux/stateActions';

import {
    GET_CALL_SESSION_DETAIL,
    GET_PROVIDER_TAGS,
    CREATE_PROVIDER_TAG,
    GET_EMOTIONS,
    UPDATE_SESSION,
    GET_CALENDAR,
    addSessionNote,
    updateSessionNote,
    deleteSessionNote,
    endGroupSession,
} from '../../services/provider';

import socket from '../../socket';

const STATUS = Object.freeze({
    CALLING: 'CALLING',
    SUMMARY: 'SUMMARY',
    END: 'END',
});

dayjs.extend(isSameOrAfter);
dayjs.extend(objectSupport);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const GroupCall = ({ history }) => {
    const theme = useTheme();
    const lgUp = useMediaQuery(theme.breakpoints.up('lg'));

    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    // const expressions = useSelector((state) => state.expressions);

    const [sessionStatus, setSessionStatus] = useState(STATUS.CALLING);
    const [selectedRightTab, setSelectedRightTab] = useState(0);

    const [selectedPatient, setSelectedPatient] = useState({});

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    /*--------------- Call Control Controller ---------------*/
    const roomInitRef = useRef();

    /*--------------- Get Emotions ---------------*/
    const [emotions, setEmotions] = useState([]);
    const { data: getEmotionsData, error: getEmotionsError } = useQuery(GET_EMOTIONS);
    useEffect(() => {
        if (getEmotionsError) {
            dispatch(alertError(`Cannot Get Emotions: ${getEmotionsError.message}`));
        }
        if (getEmotionsData && !_.isEmpty(getEmotionsData.emotions)) {
            setEmotions(getEmotionsData.emotions);
        }
    }, [getEmotionsError, getEmotionsData]);

    /*--------------- Get Session ---------------*/
    const [sessionData, setSessionData] = useState({});
    const [patients, setPatients] = useState([]);

    const {
        refetch: getSessionRefetch,
        data: getSessionData,
        loading: getSessionLoading,
        error: getSessionError,
    } = useQuery(GET_CALL_SESSION_DETAIL, {
        variables: {
            id,
        },
        skip: !id,
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot Get Session: ${getSessionError.message}`));
        }
        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            const {
                actualStartTime,
                actualEndTime,
                sessionEnd,
                cancelled,
                session_notes,
                meet,
                patient_summaries,
                name,
                description,
                startTime,
                endTime,
            } = getSessionData.session;

            // check if session end
            if (cancelled) {
                dispatch(alertError(`Session Cancelled`));
                history.push('/provider/session');
                return;
            }
            if (sessionEnd) {
                dispatch(alertSuccess(`Session Ended`));
                history.push('/provider/session');
                return;
            }

            // check if video call end
            if (actualEndTime) {
                setSessionStatus(STATUS.SUMMARY);
            }

            // check if start call for firstTime
            if (!actualStartTime) {
                updateSession({
                    variables: {
                        id: id,
                        actualStartTime: dayjs(),
                    },
                });
            }

            setSessionData(getSessionData.session);

            // only update session notes if not already exit prevent rerender
            if (_.isEmpty(notes)) {
                const newSessionNotes = Array.from(session_notes).sort((a, b) =>
                    dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1
                );

                setNotes(newSessionNotes);
            }

            setPatients(meet.patients.filter((patient) => patient.user !== null));

            // init summariesData
            const newSummariesData = [];
            meet.patients
                .filter((patient) => patient.user !== null)
                .forEach((patient) => {
                    const findSummary = patient_summaries.find(
                        (summary) =>
                            summary.patient &&
                            summary.patient?.id === patient.id &&
                            summary.joinTime
                    );

                    if (!findSummary) return;

                    newSummariesData.push({
                        id: findSummary.id,
                        note: findSummary.noteToPatient || '',
                        homework: findSummary.homework || '',
                        patient: {
                            id: patient.id,
                            patientTag: patient.patientTag,
                            user: patient.user,
                        },
                        errors: [],
                    });
                });

            setSummariesData(newSummariesData);

            setName(name);
            setDescription(description);

            setAppointmentStartTime(
                dayjs()
                    .set({
                        hour: startTime.split(':')[0],
                        minute: startTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            setAppointmentEndTime(
                dayjs()
                    .set({
                        hour: endTime.split(':')[0],
                        minute: endTime.split(':')[1],
                    })
                    .format('HH:mm')
            );
            setRules(meet.rules);
        }
    }, [getSessionError, getSessionData]);

    const handleViewPatientDetail = (id) => {
        const foundPatient = patients.find((patient) => patient.id === id);
        setSelectedPatient(foundPatient);
    };

    const rightTabsChangeHandler = (event, newValue) => {
        setSelectedRightTab(newValue);
    };

    /*--------------- Update Session ---------------*/
    const [updateSession, { data: updateSessionData, error: updateSessionError }] =
        useMutation(UPDATE_SESSION);

    useEffect(() => {
        if (updateSessionError) {
            dispatch(alertError(`Cannot update Session: ${updateSessionError.message}`));
        }
        if (updateSessionData && !_.isEmpty(updateSessionData.updateSession)) {
            getSessionRefetch();
        }
    }, [updateSessionError, updateSessionData]);

    /*--------------- End Call ---------------*/

    const endCallCallback = () => {
        socket.emit('providerEndGroupCall', {
            sessionId: id,
            patientsId: patients.map((p) => p.id),
        });

        setSessionStatus(STATUS.SUMMARY);
        setSelectedRightTab(0);
    };

    /*--------------- Resize window to fix tab width ---------------*/
    const [rightMaxHeight, setRightMaxHeight] = useState(800);
    const [tagContainerWidth, setTagContainerWidth] = useState(300);

    const handleResize = () => {
        let widthHeight = window.innerHeight;

        if (selectedRightTab === 0) {
            setRightMaxHeight(widthHeight - 365);
        } else {
            setRightMaxHeight(widthHeight - 165);
        }

        setTagContainerWidth(window.innerWidth / 3);
    };

    useEffect(() => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 1);

        handleResize();
    }, []);

    useEffect(() => {
        handleResize();
    }, [selectedRightTab]);

    window.addEventListener('resize', handleResize);

    /*--------------- Get Provider Tags ---------------*/
    const [providerTags, setProviderTags] = useState([]);
    const {
        data: getProviderTagsData,
        error: getProviderTagsError,
        refetch: getProviderTagsRefetch,
    } = useQuery(GET_PROVIDER_TAGS, {
        variables: {
            id: user.staff.id,
        },
        skip: _.isEmpty(user),
    });
    useEffect(() => {
        if (getProviderTagsError) {
            dispatch(alertError(`Cannot Get Provider Tags: ${getProviderTagsError.message}`));
        }
        if (getProviderTagsData) {
            setProviderTags(getProviderTagsData.providerTags);
        }
    }, [getProviderTagsError, getProviderTagsData]);

    /*--------------- Create Provider Tag ---------------*/
    const [createProviderTag, { data: createProviderTagData, error: createProviderTagError }] =
        useMutation(CREATE_PROVIDER_TAG);

    useEffect(() => {
        if (createProviderTagError) {
            dispatch(alertError(`Cannot Add quick note tag: ${createProviderTagError.message}`));
        }
        if (createProviderTagData) {
            getProviderTagsRefetch();
        }
    }, [createProviderTagError, createProviderTagData]);

    const handleAddProviderTag = (value) => {
        createProviderTag({
            variables: {
                id: user.staff.id,
                tag: value,
            },
        });
    };

    /*--------------- Note ---------------*/
    const selectPatientId = useSelector((state) => state.selectPatientId);
    const [notes, setNotes] = useState([]);

    useEffect(() => {
        if (selectPatientId) {
            handleAddNewNote(undefined, selectPatientId);
        }
    }, [selectPatientId]);

    const handleSaveNote = async (value) => {
        try {
            const newNoteData = {
                description: value.description,
                emotions: value.emotions,
                provider_tags: value.provider_tags,
            };

            if (value.patient) {
                newNoteData.patient = value.patient.id;
            }

            let res;

            if (value.id) {
                res = await updateSessionNote(value.id, newNoteData);
            } else {
                newNoteData.session = id;
                res = await addSessionNote(newNoteData);
            }

            if (res.status === 200) {
                const newNotes = notes.slice();
                const noteRes = res.data;
                if (value.patient) {
                    noteRes.patient = value.patient;
                }
                if (value.id) {
                    const findIndex = newNotes.findIndex((note) => note.id === value.id);
                    newNotes[findIndex] = noteRes;
                } else {
                    const findIndex = newNotes.findIndex((note) => note.tempId === value.tempId);
                    newNotes[findIndex] = noteRes;
                }
                setNotes(newNotes);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot save note: ${error}`));
        }
    };

    const handleDeleteNote = async (id) => {
        const newNotes = notes.slice();
        const foundIndex = newNotes.findIndex((note) => note.id === id);
        newNotes.splice(foundIndex, 1);

        setNotes(newNotes);

        if (id) {
            try {
                const res = await deleteSessionNote(id);

                if (res.status === 200) {
                    getSessionRefetch();
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Cannot delete note: ${error}`));
            }
        }
    };

    const handleAddNewNote = async (tag, patientId) => {
        const newNotes = notes.slice();

        const noteObj = {
            session: id,
            description: '',
            emotions: [],
        };

        if (patientId) {
            const foundPatinet = patients.find((p) => p.id === patientId);
            if (foundPatinet) {
                noteObj.patient = foundPatinet;
            } else {
                dispatch(alertError(`Client ${patientId} not found`));
            }
            dispatch(clearSelectPatientId());
        }

        if (!tag) {
            // add empty note
            noteObj.newNote = true;
            noteObj.provider_tags = [];
            noteObj.tempId = randomString({ length: 24 }).toLowerCase();
            newNotes.unshift(noteObj);
            setNotes(newNotes);
        } else {
            // create new note with tag
            try {
                const res = await addSessionNote({ ...noteObj, provider_tags: [tag.id] });
                if (res.status === 200) {
                    noteObj.provider_tags = [tag];
                    noteObj.id = res.data.id;
                    newNotes.unshift(noteObj);
                    setNotes(newNotes);
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Cannot add note: ${error}`));
            }
        }
    };

    /*--------------- Summary Sesssion ---------------*/
    const [summariesData, setSummariesData] = useState([]);

    const handleSummaryDataChange = (event, id) => {
        let name = event.target.name;
        let value = event.target.value;

        const newSummariesData = summariesData.slice();
        const findIndex = newSummariesData.findIndex((summary) => summary.id === id);
        newSummariesData[findIndex][name] = value;

        setSummariesData(newSummariesData);
    };

    /*--------------- Next Appointment ---------------*/
    const [errors, setErrors] = useState([]);
    const [noAppointment, setNoAppointment] = useState(false);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentStartTime, setAppointmentStartTime] = useState('');
    const [appointmentEndTime, setAppointmentEndTime] = useState('');
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [rules, setRules] = useState([]);

    const handleDateChange = (date) => {
        setAppointmentDate(date);
        setErrors(errors.filter((error) => error.property !== 'appointmentDate'));
    };

    const handleStartTimeChange = (time) => {
        setAppointmentStartTime(time);
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'appointmentEndTime' &&
                    error.property !== 'appointmentStartTime'
            )
        );
    };
    const handleEndTimeChabge = (time) => {
        setAppointmentEndTime(time);
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'appointmentEndTime' &&
                    error.property !== 'appointmentStartTime'
            )
        );
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
        setErrors(errors.filter((error) => error.property !== 'name'));
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
        setErrors(errors.filter((error) => error.property !== 'description'));
    };

    const handleAddPatient = (patientArr) => {
        setSelectedPatients(patientArr);
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    const handleRemovePatient = (id) => {
        setSelectedPatients(selectedPatients.filter((patient) => patient.id !== id));
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    /*--------------- Get Calendar ---------------*/
    const [calendar, setCalerdar] = useState({});
    const [overlapData, setOverlapData] = useState({});

    const {
        data: getCalendarData,
        error: getCalendarError,
        loading: getCalendarLoading,
    } = useQuery(GET_CALENDAR, {
        variables: {
            staff: user.staff.id,
            date: !_.isNull(appointmentDate) && dayjs(appointmentDate).format('YYYY-MM-DD'),
        },
        skip:
            _.isEmpty(user) ||
            _.isNull(appointmentDate) ||
            !user?.staff?.id ||
            sessionStatus !== STATUS.SUMMARY,
    });

    useEffect(() => {
        if (getCalendarError) {
            dispatch(alertError(`Cannot get calendar: ${getCalendarError.message}`));
        }

        if (getCalendarData && getCalendarData.calendars.length > 1) {
            dispatch(alertError(`More than one calendar found`));
            return;
        }

        if (getCalendarData) {
            if (_.isEmpty(getCalendarData.calendars)) {
                setCalerdar({});
            } else {
                setCalerdar(getCalendarData.calendars[0]);
            }
        }
    }, [getCalendarData, getCalendarError]);

    const onClear = () => {
        const newSummariesData = summariesData.slice();
        newSummariesData.forEach((summary, index) => {
            newSummariesData[index]['note'] = '';
            newSummariesData[index]['homework'] = '';
        });

        setSummariesData(newSummariesData);

        setName('');
        setDescription('');
        setAppointmentDate(null);
        setAppointmentStartTime('');
        setAppointmentEndTime('');
        setSelectedPatients([]);
    };

    const onClickSave = () => {
        setOpen(true);
    };

    const onClickCancel = () => {
        setOpen(false);
    };

    const onCancelConfirm = () => {
        setOverlapData({});
        onClickCancel();
    };

    const onClickConfirm = async () => {
        try {
            if (!noAppointment) {
                const newErrors = [];

                if (name === '') {
                    newErrors.push({
                        property: 'name',
                        message: 'Please provide a valid Name.',
                    });
                }

                if (description === '') {
                    newErrors.push({
                        property: 'description',
                        message: 'Please provide a valid Description.',
                    });
                }

                if (appointmentDate === null) {
                    newErrors.push({
                        property: 'appointmentDate',
                        message: 'Please provide a valid Date.',
                    });
                }

                if (appointmentStartTime === '') {
                    newErrors.push({
                        property: 'appointmentStartTime',
                        message: 'Please provide a valid Start Time.',
                    });
                }

                if (appointmentEndTime === '') {
                    newErrors.push({
                        property: 'appointmentEndTime',
                        message: 'Please provide a valid End Time.',
                    });
                }

                if (_.isEmpty(selectedPatients)) {
                    newErrors.push({
                        property: 'selectedPatients',
                        message: 'Please provide a valid Client.',
                    });
                }

                if (
                    dayjs()
                        .set({
                            hour: appointmentStartTime.split(':')[0],
                            minute: appointmentStartTime.split(':')[1],
                        })
                        .isSameOrAfter(
                            dayjs().set({
                                hour: appointmentEndTime.split(':')[0],
                                minute: appointmentEndTime.split(':')[1],
                            }),
                            'minute'
                        )
                ) {
                    newErrors.push({
                        property: 'appointmentEndTime',
                        message: 'End Time must be after Start Time.',
                    });
                }

                if (
                    appointmentDate !== null &&
                    (dayjs(appointmentDate)
                        .set({
                            hour: appointmentStartTime.split(':')[0],
                            minute: appointmentStartTime.split(':')[1],
                        })
                        .isSameOrBefore(dayjs()) ||
                        dayjs(appointmentDate)
                            .set({
                                hour: appointmentEndTime.split(':')[0],
                                minute: appointmentEndTime.split(':')[1],
                            })
                            .isSameOrBefore(dayjs()))
                ) {
                    newErrors.push({
                        property: 'appointmentEndTime',
                        message: 'Session must be in the future.',
                    });
                }

                if (!_.isEmpty(newErrors)) {
                    setOpen(false);
                    setErrors(newErrors);
                    return;
                }

                if (!_.isEmpty(calendar)) {
                    const { meets } = calendar;

                    const startTimeOverlap = meets?.find((meet) =>
                        dayjs()
                            .set({
                                hour: appointmentStartTime.split(':')[0],
                                minute: appointmentStartTime.split(':')[1],
                            })
                            .isBetween(
                                dayjs().set({
                                    hour: meet.startTime.split(':')[0],
                                    minute: meet.startTime.split(':')[1],
                                }),
                                dayjs().set({
                                    hour: meet.endTime.split(':')[0],
                                    minute: meet.endTime.split(':')[1],
                                }),
                                'minute',
                                '[]'
                            )
                    );
                    const endTimeOverlap = meets?.find((meet) =>
                        dayjs()
                            .set({
                                hour: appointmentEndTime.split(':')[0],
                                minute: appointmentEndTime.split(':')[1],
                            })
                            .isBetween(
                                dayjs().set({
                                    hour: meet.startTime.split(':')[0],
                                    minute: meet.startTime.split(':')[1],
                                }),
                                dayjs().set({
                                    hour: meet.endTime.split(':')[0],
                                    minute: meet.endTime.split(':')[1],
                                }),
                                'minute',
                                '[]'
                            )
                    );
                    if (startTimeOverlap) {
                        setOverlapData(startTimeOverlap);
                        setOpen(false);
                        return;
                    }
                    if (endTimeOverlap) {
                        setOverlapData(endTimeOverlap);
                        setOpen(false);
                        return;
                    }
                }
            }

            setLoading(true);
            setOpen(false);

            const data = {
                sessionId: id,
                patientSummaries: summariesData.map((summary) => ({
                    id: summary.id,
                    noteToPatient: summary.note,
                    homework: summary.homework,
                })),
            };

            if (!noAppointment) {
                data.nextAppointment = {
                    hospital: user.staff.hospital.id,
                    staff: user.staff.id,
                    patients: selectedPatients.map((p) => p.id),
                    name: name.trim(),
                    description: description.trim(),
                    date: dayjs(appointmentDate).format('YYYY-MM-DD'),
                    startTime: dayjs()
                        .set('date', dayjs(appointmentDate).get('date'))
                        .set('hour', appointmentStartTime.split(':')[0])
                        .set('minute', appointmentStartTime.split(':')[1])
                        .format('HH:mm:ss.SSS'),
                    endTime: dayjs()
                        .set('date', dayjs(appointmentDate).get('date'))
                        .set('hour', appointmentEndTime.split(':')[0])
                        .set('minute', appointmentEndTime.split(':')[1])
                        .format('HH:mm:ss.SSS'),
                    rules,
                };
            }

            const res = await endGroupSession(data);

            if (res.status !== 200) {
                throw new Error(res);
            }

            setSessionStatus(STATUS.END);

            window.onbeforeunload = null;
            window.close();
        } catch (error) {
            dispatch(alertError(`Cannot Save: ${error.response?.data?.message}`));
        }

        setLoading(false);
    };

    useEffect(() => {
        socket.on('patientJoinGroupCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
    }, []);

    return sessionStatus === STATUS.END ? (
        <div
            style={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text $bold>Session Ended please close this window</Text>
        </div>
    ) : (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns:
                    sessionStatus === STATUS.SUMMARY
                        ? '1fr'
                        : sessionStatus === STATUS.CALLING
                        ? '2fr 1fr'
                        : '1fr 2fr',
                minHeight: '100vh',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {(getSessionLoading || loading) && <Progress />}
            <ConfirmDialog
                open={open}
                contentText="Confirm Save?"
                cancelText="Cancel"
                onCancel={onClickCancel}
                confirmText="Save"
                onConfirm={onClickConfirm}
            />
            {sessionStatus === STATUS.CALLING && (
                <Main
                    ref={roomInitRef}
                    type="group"
                    isProvider={true}
                    faceDetection={true}
                    useMicCamera={true}
                />
            )}

            {sessionStatus === STATUS.CALLING && (
                <div
                    style={{
                        border: '1px solid white',
                        position: 'relative',
                        height: '100vh',
                    }}
                >
                    <StyledBackground />

                    {_.isEmpty(selectedPatient) ? (
                        <div>
                            <Text
                                $fontSize="20px"
                                $bold
                                $ellipsis
                                style={{ margin: '20px 20px 0' }}
                            >
                                Activity name - {sessionData?.meet?.name}
                            </Text>
                            <Tabs
                                value={selectedRightTab}
                                onChange={rightTabsChangeHandler}
                                indicatorColor="primary"
                                TabIndicatorProps={{ style: { height: '4px' }, children: <span /> }}
                                style={{
                                    marginTop: '10px',
                                    marginBottom: '16px',
                                    paddingLeft: '20px',
                                    paddingRight: '20px',
                                }}
                            >
                                <PatientDetailTab
                                    label="Take Note"
                                    $active={selectedRightTab === 0}
                                />
                                <PatientDetailTab
                                    label="Client Medical Record"
                                    $active={selectedRightTab === 1}
                                    style={{ marginLeft: '10px' }}
                                />
                            </Tabs>

                            <div style={{ maxHeight: rightMaxHeight, overflowY: 'auto' }}>
                                {selectedRightTab === 0 && (
                                    <ProviderNote
                                        data={notes}
                                        tags={providerTags}
                                        emotions={emotions}
                                        handleSaveNote={handleSaveNote}
                                        handleDeleteNote={handleDeleteNote}
                                    />
                                )}
                                {selectedRightTab === 1 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            rowGap: '10px',
                                            paddingLeft: '20px',
                                            paddingRight: '20px',
                                            paddingBottom: '60px',
                                        }}
                                    >
                                        {patients?.length > 0 &&
                                            patients.map((patient) => (
                                                <PatientCard
                                                    data={patient}
                                                    key={patient.id}
                                                    onDetail={handleViewPatientDetail}
                                                />
                                            ))}
                                    </div>
                                )}
                            </div>

                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    left: 0,
                                }}
                            >
                                {selectedRightTab === 0 && (
                                    <QuickNoteTag
                                        tagContainerWidth={tagContainerWidth}
                                        onAddNote={handleAddNewNote}
                                        onAddNewTag={handleAddProviderTag}
                                        data={providerTags}
                                    />
                                )}
                                <CallController
                                    isProvider={true}
                                    handleMic={() => roomInitRef.current.micControl()}
                                    handleCamera={() => roomInitRef.current.webcamControl()}
                                    onEndCall={() => {
                                        try {
                                            roomInitRef.current.providerCloseRoom(endCallCallback);
                                        } catch (error) {
                                            endCallCallback();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
                            <div style={{ margin: '20px 20px 0' }}>
                                <GoBackButton $text onClick={() => setSelectedPatient(null)} />
                            </div>
                            <PatientMedicalRecord data={selectedPatient} />
                        </div>
                    )}
                </div>
            )}

            {sessionStatus === STATUS.SUMMARY && (
                <div
                    style={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: sessionStatus === STATUS.CALLING ? '1fr' : '1fr 2fr',
                    }}
                >
                    <StyledDialog
                        scroll="body"
                        open={!_.isEmpty(overlapData)}
                        onClose={onCancelConfirm}
                        aria-labelledby="warningDialog"
                        aria-describedby="warning dialog"
                        PaperProps={{ style: { borderRadius: '16px' } }}
                    >
                        <OverlapCard data={overlapData} onCancelConfirm={onCancelConfirm} />
                    </StyledDialog>
                    <div
                        style={{
                            border: '1px solid white',
                            filter: 'drop-shadow(-4px 0px 8px rgba(174, 180, 210, 0.4))',
                            padding: '5px 20px 80px',
                            position: 'relative',
                        }}
                    >
                        <Text $fontSize="20px" $bold>
                            Next Appointment <Span $color="gray2">(Optional)</Span>
                        </Text>
                        <Checkbox
                            label={
                                <span style={{ marginLeft: '10px', fontSize: '14px' }}>
                                    No follow up
                                </span>
                            }
                            checked={noAppointment}
                            name="noAppointment"
                            onChange={(e) => {
                                setNoAppointment(e.target.checked);
                                setErrors([]);
                            }}
                        />
                        <KeyboardDatePicker
                            disablePast
                            id="newAppointmentDate"
                            label={
                                <Span $color="black">
                                    Date <Span $color="pink">*</Span>
                                </Span>
                            }
                            value={appointmentDate}
                            onChange={(date) => handleDateChange(date)}
                            disabled={noAppointment || loading}
                        />
                        {errors.find((value) => value.property === 'appointmentDate') && (
                            <StyledFormHelperText error>
                                {
                                    errors.find((value) => value.property === 'appointmentDate')
                                        ?.message
                                }
                            </StyledFormHelperText>
                        )}

                        <div style={{ display: 'flex', columnGap: '10px' }}>
                            <TimePicker
                                id="startTime"
                                label="Start time"
                                required
                                placeholder="Select"
                                value={appointmentStartTime}
                                onChange={handleStartTimeChange}
                                disabled={noAppointment || loading}
                            />

                            <TimePicker
                                id="endTime"
                                label="End time"
                                required
                                placeholder="Select"
                                value={appointmentEndTime}
                                onChange={handleEndTimeChabge}
                                disabled={noAppointment || loading}
                            />
                        </div>

                        <div>
                            {errors.find((value) => value.property === 'appointmentStartTime') && (
                                <StyledFormHelperText error>
                                    {
                                        errors.find(
                                            (value) => value.property === 'appointmentStartTime'
                                        )?.message
                                    }
                                </StyledFormHelperText>
                            )}
                            {errors.find((value) => value.property === 'appointmentEndTime') && (
                                <StyledFormHelperText error>
                                    {
                                        errors.find(
                                            (value) => value.property === 'appointmentEndTime'
                                        )?.message
                                    }
                                </StyledFormHelperText>
                            )}
                        </div>

                        <Input
                            label="Session Name"
                            required
                            placeholder="Session Name"
                            value={name}
                            onChange={handleNameChange}
                            helperText={errors.find((value) => value.property === 'name')?.message}
                            disabled={noAppointment || loading}
                            max={100}
                        />

                        <TextField
                            required
                            id="description"
                            name="description"
                            label="Description"
                            placeholder="Description"
                            value={description}
                            onChange={handleDescriptionChange}
                            helperText={
                                errors.find((value) => value.property === 'description')?.message
                            }
                            disabled={noAppointment || loading}
                            inputProps={{ maxLength: 2000 }}
                        />
                        <SelectPatientCard
                            layout="NEXT_GROUP"
                            errors={errors}
                            loading={loading || getCalendarLoading}
                            selectedPatients={selectedPatients}
                            handleAddPatient={handleAddPatient}
                            handleRemovePatient={handleRemovePatient}
                            options={patients}
                        />
                    </div>
                    <div
                        style={{
                            overflowY: 'auto',

                            maxHeight: '100vh',
                            padding: '24px 24px 80px',
                        }}
                    >
                        <Text $fontSize="20px" $bold style={{ marginBottom: '20px' }}>
                            Homework
                        </Text>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: lgUp ? 'repeat(2,1fr)' : '1fr',
                                gap: '24px',
                            }}
                        >
                            {summariesData.map((summary) => (
                                <HomeworkCard
                                    key={summary.id}
                                    data={summary}
                                    onDetail={() => console.log('HomeworkCard', summary.patient)}
                                    onChange={handleSummaryDataChange}
                                />
                            ))}
                        </div>
                    </div>
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            columnGap: '20px',
                            backgroundColor: theme.palette.common.white,
                            opacity: 0.9,
                            border: `1px solid ${theme.palette.common.white}`,
                            boxSizing:
                                '0px -2px 4px rgba(174, 180, 210, 0.4), inset 0px -2px 4px rgba(174, 180, 210, 0.2)',
                            padding: '15px 24px',
                        }}
                    >
                        <OutlinedButton $width="90px" onClick={onClear}>
                            Clear
                        </OutlinedButton>
                        <ContainedButton
                            $width="90px"
                            onClick={onClickSave}
                            disabled={getCalendarLoading}
                        >
                            Save
                        </ContainedButton>
                    </div>
                </div>
            )}
        </div>
    );
};

GroupCall.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default GroupCall;
